import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { notifySubscriptionChanged } from '../subscriptionChanged';
import { formatUtcDate } from '../formatDate';
import type { CompanySubscriptionResponse } from '../types';
import { CHECKOUT_MAX_ATTEMPTS, CHECKOUT_POLL_INTERVAL_MS, checkoutOutcome } from '../billing/checkoutOutcome';
import '../billing.css';

type Phase = 'confirming' | 'confirmed' | 'failed' | 'timeout';

/**
 * `/settings/billing/checkout-complete` — where the provider's hosted checkout sends the customer back.
 *
 * It NEVER trusts the URL it was opened with: no query parameter (a session id, a "success" flag, anything) is read
 * or believed, because anyone can type a URL. Instead it asks the server — repeatedly, within a bound — for the
 * company's subscription and shows the paid state only once the server reports it. The server learns about the
 * payment from the provider's verified webhook, which can arrive a little after the customer does, so "still
 * confirming" is a normal, honest state, and a timeout ends with a clear next step rather than a guess.
 */
export function CheckoutCompletePage({
  pollIntervalMs = CHECKOUT_POLL_INTERVAL_MS,
  maxAttempts = CHECKOUT_MAX_ATTEMPTS,
}: {
  pollIntervalMs?: number;
  maxAttempts?: number;
}) {
  const [phase, setPhase] = useState<Phase>('confirming');
  const [subscription, setSubscription] = useState<CompanySubscriptionResponse | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [runId, setRunId] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll(count: number) {
      if (cancelled) return;
      setAttempt(count + 1);
      try {
        const current = await subscriptionGateway.getCurrentSubscription();
        if (cancelled) return;
        const outcome = checkoutOutcome(current);
        if (outcome !== 'pending') {
          setSubscription(current);
          setPhase(outcome === 'paid' ? 'confirmed' : 'failed');
          notifySubscriptionChanged();
          return;
        }
      } catch {
        // A failed lookup is not "paid" and not "failed": keep asking until the bound is reached.
      }
      if (cancelled) return;
      if (count + 1 >= maxAttempts) {
        setPhase('timeout');
        return;
      }
      timer.current = setTimeout(() => void poll(count + 1), pollIntervalMs);
    }

    void poll(0);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [pollIntervalMs, maxAttempts, runId]);

  const checkAgain = useCallback(() => {
    setPhase('confirming');
    setAttempt(0);
    setRunId((n) => n + 1);
  }, []);

  return (
    <section className="card card--wide" aria-labelledby="checkout-complete-heading" data-testid="checkout-complete" data-phase={phase}>
      {phase === 'confirming' && (
        <>
          <h2 id="checkout-complete-heading">Confirming your payment</h2>
          <p role="status" aria-live="polite" data-testid="checkout-confirming">
            We are still confirming your payment. This usually takes a few seconds. Please keep this page open; we will update it
            as soon as your subscription is active.
          </p>
          <p className="muted">Checked {attempt} {attempt === 1 ? 'time' : 'times'} so far. Reloading this page is safe: it only checks, and never starts a payment.</p>
        </>
      )}

      {phase === 'confirmed' && subscription && (
        <>
          <h2 id="checkout-complete-heading">Your subscription is active</h2>
          <p role="status" aria-live="polite">
            Thank you. <strong>{subscription.planName}</strong> is active
            {subscription.currentPeriodEndUtc ? ` through ${formatUtcDate(subscription.currentPeriodEndUtc)}` : ''}. Your invoice
            will appear in your billing history.
          </p>
          <div className="inline-form">
            <Link to="/settings/billing"><button type="button">Go to billing</button></Link>
            <Link to="/dashboards/admin-dashboard" className="link">Go to dashboard</Link>
          </div>
        </>
      )}

      {phase === 'failed' && (
        <>
          <h2 id="checkout-complete-heading">Your payment did not go through</h2>
          <p role="alert">
            Your payment could not be collected, so nothing was charged. Please update your payment method and try again.
          </p>
          <div className="inline-form">
            <Link to="/settings/billing"><button type="button">Go to billing</button></Link>
          </div>
        </>
      )}

      {phase === 'timeout' && (
        <>
          <h2 id="checkout-complete-heading">We could not confirm your payment yet</h2>
          <p role="alert" data-testid="checkout-timeout">
            We are still confirming your payment and it is taking longer than usual. If you were charged, your invoice will appear in
            your billing history once it is confirmed. Please check your billing page in a few minutes, or contact billing support
            and we will look into it.
          </p>
          <div className="inline-form">
            <button type="button" onClick={checkAgain}>Check again</button>
            <Link to="/settings/billing" className="link">Go to billing</Link>
          </div>
        </>
      )}
    </section>
  );
}
